import { ReactNode } from "react";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { PalletFigures, PalletStatus } from "../../types/pallet-summary.type";
import { SURFACE } from "../common/designTokens";
import PalletStatusChip from "./PalletStatusChip";
import { formatPalletCount } from "./palletSummary";

export interface PalletColumn<Row> {
  label: string;
  align?: TableCellProps["align"];
  render: (row: Row) => ReactNode;
}

type PalletRow = PalletFigures & { status: PalletStatus };

interface PalletTableProps<Row extends PalletRow> {
  leadColumns: PalletColumn<Row>[];
  /** หมายเหตุ on a route or a trip, then ดูรายละเอียด on a level with one below it. */
  trailingColumns: PalletColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** The server's totals, drawn as a รวม row under the rows. */
  totals?: PalletFigures;
}

const FIGURE_COLUMNS = ["ถาดขึ้น", "ถาดกลับ", "ส่วนต่าง"];
const STATUS_COLUMN = "สถานะ";
/** สถานะ follows the figures on every level, ahead of the level's trailing columns. */
const STATUS_COLUMN_COUNT = 1;
const TOTAL_ROW_LABEL = "รวม";
const EMPTY_TEXT = "ไม่พบข้อมูล";
const DETAIL_LABEL = "ดูรายละเอียด";

const HEAD_SX = { "& th": { bgcolor: SURFACE.head, fontWeight: 600, whiteSpace: "nowrap" } };
const TOTAL_ROW_SX = { "& td": { bgcolor: SURFACE.line2, fontWeight: 700 } };

function FigureCells({ figures }: Readonly<{ figures: PalletFigures }>) {
  return (
    <>
      <TableCell align="right">{formatPalletCount(figures.pallet_out)}</TableCell>
      <TableCell align="right">{formatPalletCount(figures.pallet_in)}</TableCell>
      <TableCell align="right">{formatPalletCount(figures.pallet_diff)}</TableCell>
    </>
  );
}

function DetailButton({ onClick }: Readonly<{ onClick: () => void }>) {
  return (
    <Tooltip title={DETAIL_LABEL}>
      <IconButton size="small" color="primary" aria-label={DETAIL_LABEL} onClick={onClick}>
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

/** The ดูรายละเอียด column of a level that opens the one below it. */
export function detailColumn<Row>(onOpen: (row: Row) => void): PalletColumn<Row> {
  const handleOpen = (row: Row) => () => onOpen(row);
  return {
    label: DETAIL_LABEL,
    align: "center",
    render: (row) => <DetailButton onClick={handleOpen(row)} />,
  };
}

/**
 * The table every level shares: the level's own lead columns, then ถาดขึ้น · ถาดกลับ · ส่วนต่าง ·
 * สถานะ, then its trailing columns.
 */
export default function PalletTable<Row extends PalletRow>({
  leadColumns,
  trailingColumns,
  rows,
  rowKey,
  totals,
}: Readonly<PalletTableProps<Row>>) {
  const columnsAfterFigures = STATUS_COLUMN_COUNT + trailingColumns.length;
  const columnCount = leadColumns.length + FIGURE_COLUMNS.length + columnsAfterFigures;
  // An empty level shows ไม่พบข้อมูล rather than a รวม row of zeros.
  const totalRowFigures = rows.length > 0 ? totals : undefined;

  return (
    <Paper>
      <TableContainer>
        <Table size="small">
          <TableHead sx={HEAD_SX}>
            <TableRow>
              {leadColumns.map((column) => (
                <TableCell key={column.label} align={column.align}>
                  {column.label}
                </TableCell>
              ))}
              {FIGURE_COLUMNS.map((label) => (
                <TableCell key={label} align="right">
                  {label}
                </TableCell>
              ))}
              <TableCell align="center">{STATUS_COLUMN}</TableCell>
              {trailingColumns.map((column) => (
                <TableCell key={column.label} align={column.align}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  <Typography color="text.secondary" py={3}>
                    {EMPTY_TEXT}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={rowKey(row)} hover>
                {leadColumns.map((column) => (
                  <TableCell key={column.label} align={column.align}>
                    {column.render(row)}
                  </TableCell>
                ))}
                <FigureCells figures={row} />
                <TableCell align="center">
                  <PalletStatusChip status={row.status} />
                </TableCell>
                {trailingColumns.map((column) => (
                  <TableCell key={column.label} align={column.align}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {totalRowFigures && (
              <TableRow sx={TOTAL_ROW_SX}>
                <TableCell colSpan={leadColumns.length}>{TOTAL_ROW_LABEL}</TableCell>
                <FigureCells figures={totalRowFigures} />
                <TableCell colSpan={columnsAfterFigures} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
